/**
 * Construit le titre + les colonnes de sujet d'une discussion à créer, selon la
 * cible choisie (« Discuter de cet exercice / cette soumission », un chapitre,
 * ou un fil libre). Fonction PURE : la validation finale (bornes) reste faite
 * par le CHECK SQL (`discussions_subject_matches_scope_chk`), mais on
 * tronque/nettoie ici pour éviter un aller-retour réseau qui échouerait juste
 * pour un titre trop long.
 *
 * ⚠️ Portée `free` : AUCUNE saisie de titre — jamais un champ à remplir. La
 * discussion naît avec `DISCUSSION_PLACEHOLDER_TITLE` ; l'edge fn
 * `discussion-generate-title` (étape LLM `discussion_title_generate`,
 * migration 0268) en déduit un titre court dès le premier message, comme le
 * titrage automatique d'une conversation Claude/ChatGPT.
 */

import type { DiscussionSubjectScope } from './types';

const TITLE_MAX_LENGTH = 120;

/** Titre posé à la création d'une discussion `free` — MIROIR :
 * `_shared/prompts/discussion-title-generate.ts` (garder synchro). */
export const DISCUSSION_PLACEHOLDER_TITLE = 'Nouvelle discussion';

export type DiscussionSubjectInput =
  | { scope: 'exercise'; exerciseId: string; exerciseTitle: string }
  | { scope: 'submission'; submissionId: string; submittedAt: string }
  | { scope: 'chapter'; chapterId: string; chapterTitle: string }
  | { scope: 'free' };

export interface DiscussionSubjectPayload {
  title: string;
  subjectScope: DiscussionSubjectScope;
  exerciseId: string | null;
  submissionId: string | null;
  chapterId: string | null;
}

const SUBMISSION_DATE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  day: 'numeric',
  month: 'long',
});

function clampTitle(title: string): string {
  const trimmed = title.trim();
  const safe = trimmed.length > 0 ? trimmed : 'Discussion';
  return safe.length > TITLE_MAX_LENGTH ? safe.slice(0, TITLE_MAX_LENGTH) : safe;
}

export function buildDiscussionSubject(input: DiscussionSubjectInput): DiscussionSubjectPayload {
  switch (input.scope) {
    case 'exercise':
      return {
        title: clampTitle(`Exercice : ${input.exerciseTitle}`),
        subjectScope: 'exercise',
        exerciseId: input.exerciseId,
        submissionId: null,
        chapterId: null,
      };
    case 'submission':
      return {
        title: clampTitle(
          `Exercice envoyé le ${SUBMISSION_DATE.format(new Date(input.submittedAt))}`,
        ),
        subjectScope: 'submission',
        exerciseId: null,
        submissionId: input.submissionId,
        chapterId: null,
      };
    case 'chapter':
      return {
        title: clampTitle(`Chapitre : ${input.chapterTitle}`),
        subjectScope: 'chapter',
        exerciseId: null,
        submissionId: null,
        chapterId: input.chapterId,
      };
    case 'free':
      return {
        title: DISCUSSION_PLACEHOLDER_TITLE,
        subjectScope: 'free',
        exerciseId: null,
        submissionId: null,
        chapterId: null,
      };
  }
}
